import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatPlatformName, getIcon } from "../../constants/constants";
import { Icons } from "../../constants/icon-library";
import LoadingComponent from "../../components/common/LoadingComponent";
import { getIntegrationBySource } from "../../features/meeting-integration/meetingIntegrationSlice";
import { fetchIntegrationSettingsByUserId } from "../../features/integration-setting/integrationSettingSlice";
import {
  createAuthState,
  getIntegrationAuthUrl,
} from "../Integrations/Integrations.utils";
import { useNavigate, useParams } from "react-router-dom";

function Integrations() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const { source } = useParams();
  const isNoteTakerSelected = source === "note-taker";

  const { integrationSettings, loading: settingLoading } = useSelector(
    (state) => state.integrationSetting,
  );

  const { sourceIntegrations = [], loading: integrationsLoading } = useSelector(
    (state) => state.meetingIntegrations,
  );

  useEffect(() => {
    dispatch(getIntegrationBySource(source));
  }, [dispatch, source]);

  useEffect(() => {
    if (user?.uuid) {
      dispatch(fetchIntegrationSettingsByUserId());
    }
  }, [user?.uuid, dispatch]);

  const loading = settingLoading || integrationsLoading;

  const installedMap = useMemo(() => {
    const map = {};
    if (Array.isArray(integrationSettings)) {
      integrationSettings.forEach((s) => {
        const uuid = s?.integrationId?.uuid;
        if (uuid) {
          map[uuid] = s;
        }
      });
    }
    return map;
  }, [integrationSettings]);

  const handleInstall = (integration) => {
    if (!integration?.isOauthRequired) {
      navigate(`/onboarding/setup/${integration.uuid}/${integration.name}`);
      return;
    }

    const isOnboarding = true;
    const state = createAuthState(user, integration, isOnboarding);

    const url = getIntegrationAuthUrl(
      integration.uuid,
      integration.name,
      state,
    );
    window.location.href = url;
  };

  const connectedCount = sourceIntegrations.filter((item) => {
    return installedMap[item.uuid];
  }).length;

  return (
    <div className="container py-5 mt-4" style={{ maxWidth: "1400px" }}>
      <div className="text-center mb-5">
        <h2 className="fw-bold">
          {isNoteTakerSelected
            ? "Choose your AI Meetings Note Taker Platform"
            : "Choose Your Team Communication Platform"}
        </h2>
        <p className="text-muted mb-0 mt-2">
          {isNoteTakerSelected
            ? "Connect your preferred AI meeting assistant to automatically import transcripts, recordings, summaries, and action items. Turn meeting discussions into structured tasks and workflows without manual effort."
            : "Connect your communication platform to import conversations, discussions, and decisions automatically. Turn messages into actionable tasks and keep work organized across your organization."}
        </p>
      </div>

      {loading ? (
        <LoadingComponent loading={true} size={15} />
      ) : (
        <div className="row g-4 justify-content-center">
          {sourceIntegrations.map((item) => {
            const key = item.name.toLowerCase();
            const iconSrc = getIcon(key);

            const isComingSoon = item.status === "coming_soon";
            const installedSetting = installedMap[item?.uuid];

            const isInstalled = !!installedSetting;

            const displayName = installedSetting?.integrationName || item.name;
            return (
              <div key={item.name} className="col-lg-3 col-md-4 col-sm-6">
                <div
                  className="card h-100 text-center shadow-sm p-3"
                  style={{
                    borderRadius: "18px",
                    minHeight: "220px",
                    transition: "0.2s",
                  }}
                >
                  <div className="card-body d-flex flex-column align-items-center justify-content-center">
                    <div
                      className="rounded-circle bg-secondary d-flex align-items-center justify-content-center mb-3"
                      style={{ width: "100px", height: "100px" }}
                    >
                      <img
                        src={iconSrc}
                        alt={item.name}
                        style={{ width: "48px", height: "48px" }}
                      />
                    </div>

                    <h5 className="fw-semibold mb-3">
                      {formatPlatformName(displayName)}
                    </h5>

                    <button
                      className={`btn w-100 ${
                        isInstalled ? "btn-success" : "btn-primary"
                      }`}
                      onClick={() =>
                        !isComingSoon && !isInstalled && handleInstall(item)
                      }
                    >
                      <i
                        className={`wb ${Icons(
                          isInstalled
                            ? "check"
                            : isComingSoon
                              ? "clock"
                              : "install",
                        )} me-1`}
                      />
                      {isInstalled
                        ? "Connected"
                        : isComingSoon
                          ? "Coming Soon"
                          : "Install"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="d-flex justify-content-end align-items-center mt-4 pt-4 border-top">

        <div>
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-link text-muted"
              onClick={() => navigate("/onboarding/apps")}
              style={{ fontSize: "1.1rem" }}
            >
              Skip
            </button>
            <button
              className="btn btn-primary px-4"
              disabled={connectedCount === 0}
              onClick={() => navigate("/onboarding/apps")}
            >
              Continue →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Integrations;
